import {CardElement, ElementsConsumer} from '@stripe/react-stripe-js';
import {
  Stripe,
  StripeCardElementOptions,
  StripeElements,
} from '@stripe/stripe-js/types/stripe-js';
import {observer} from 'mobx-react';
import {SyntheticEvent, useMemo, useState} from 'react';
import {Controller} from 'react-hook-form';
import {parseCurrencyValue} from '../../../../../../../../../constants';
import {useStoreContext} from '../../../../../../../../../store/store-context';
import {Button} from '../../../../../../../../../ui/atoms/button';
import {Helper} from '../../../../../../../../../ui/atoms/helper';
import dayjs from 'dayjs';
import {ThickCheckIcon} from '../../../../../../../../../ui/atoms/icons/check';
import {FlexRowSpaceBetween, FlexRowEnd, FlexRow} from '@ui/style/styles';
import {NoBorderButton} from '@pages/dashboard/reporting/reporting-pages/reporting-overview/styles';
import {Label} from '../../../../../../../../../ui/atoms/label';
import {
  HorizontalSpacer,
  VerticalSpacer,
} from '../../../../../../../../../ui/atoms/spacer';
import {
  Body1,
  Body2,
  CTA,
  Headline2,
  Headline3,
  Headline4,
} from '../../../../../../../../../ui/atoms/typography';
import {SecondaryBreadcrumb} from '../../../../../../../../../ui/molecules/breadcrumb';
import {RadioField} from '../../../../../../../../../ui/molecules/field/radiofield';
import {TextField} from '../../../../../../../../../ui/molecules/field/textfield';
import {SelectField} from '../../../../../../../../../ui/molecules/select/selectfield';
import {useNavigate} from 'react-router';
import {useSearchParams} from 'react-router-dom';
import {useBillingHook} from '../../../admin-billing.hooks';
import {
  BillingCycleInnerWrapper,
  BillingCycleOuterWrapper,
  BillingActionButton,
  CardInfoDiv,
  MultiRadioSelectDiv,
  PriceBoxDiv,
  PriceHelperInfoDiv,
} from '../billing-not-active.styles';
import {ICity, ICountry} from 'country-state-city/dist/lib/interface';
import {CancelIcon} from '../../../../../../../../../ui/atoms/icons';
import {CardDetailsDiv} from '../../billing-active/billing-active.styles';
import {capitalize} from '@utils';
import {Link} from '@ui/atoms/link';
import {UpdateCard} from '../../billing-active/components/update-card';
import {useBillingCycleState} from './billing-cycle-state-hook';
import {
  isBillingAddressComplete,
  returnToPlanSelection,
} from './billing-cycle.utils';
import {calculateBillingPrices} from '@utils/billing';
import {DefaultTheme, useTheme} from 'styled-components';

interface IInjectedBillingCycle {
  stripe: Stripe | null;
  elements: StripeElements | null;
}
export const getBillingCardOptions = (
  colors: Pick<
    DefaultTheme['colors'],
    'purple300' | 'textBody' | 'white' | 'red300'
  >,
): StripeCardElementOptions => ({
  iconStyle: 'solid',
  style: {
    base: {
      iconColor: colors.purple300,
      color: colors.textBody,
      fontWeight: 500,
      fontFamily: 'Roboto, Open Sans, Segoe UI, sans-serif',
      fontSize: '16px',
      fontSmoothing: 'antialiased',
      ':-webkit-autofill': {
        color: colors.textBody,
      },
      '::placeholder': {
        color: colors.textBody,
      },
      backgroundColor: colors.white,
    },
    invalid: {
      iconColor: colors.red300,
      color: colors.red300,
    },
  },
});

export const InjectedBillingCycle = observer(
  ({stripe, elements}: IInjectedBillingCycle) => {
    const theme = useTheme();
    const {
      billingRoutesStore: {activateInactiveBillingTabs},
      billingsStore: {
        selectedBillingPlan,
        setSelectedBillingPlan,
        updateShowNonActiveBilling,
      },
    } = useStoreContext();

    const [searchParams] = useSearchParams();

    const {
      handleSubmitCard,
      errorsCard,
      controlCard,
      isSubmittingCard,
      handleFormValueChangeCard,
      stripeError,
      setStripeError,
      setCardComplete,
      updateAddressDetails,
      addressDetails,
      handleSubmitPaymentMethod,
      validateCoupon,
      couponDiscount,
      setCouponDiscount,
      setValidCoupon,
    } = useBillingHook();

    const {
      countries,
      setSelectedCountry,
      cities,
      addressSaved,
      setAddressSaved,
      line1,
      setLine1,
      openDrawer,
      setOpenDrawer,
      coupon,
      setCoupon,
    } = useBillingCycleState();

    const {billingsStore} = useStoreContext();

    const billingPlan = searchParams.get('billingPlan');

    const chosenBillingPlanType =
      billingPlan || billingsStore.chosenBillingPlanType;

    const isLifeTimePlan = chosenBillingPlanType === 'lifetime';
    const isFreeTierSubscription = searchParams.get('free-trial');

    const navigate = useNavigate();

    const currencySymbol = parseCurrencyValue(
      (billingsStore.billingPlans as any)?.[chosenBillingPlanType]?.currency,
    );

    const numSeats = isLifeTimePlan ? 500 : billingsStore.numSeats || 1;

    const calculatePrices = () => {
      if (billingsStore.billingPlans)
        return calculateBillingPrices({
          billingPlans: billingsStore.billingPlans,
          chosenBillingPlanType,
          selectedBillingCycle: billingsStore.selectedBillingPlan,
          numSeats,
          couponDiscount: couponDiscount,
        });

      return {
        subtotal: 0,
        annualDiscountPercent: 0,
        annualDiscountAmount: 0,
        couponDiscountAmount: 0,
        dueToday: 0,
      };
    };

    const {
      subtotal = 0,
      annualDiscountPercent = 0,
      annualDiscountAmount = 0,
      couponDiscountAmount = 0,
      dueToday = 0,
    } = calculatePrices();

    const handleAddressSave = () => {
      setAddressSaved(true);
    };

    const handleSubmit = handleSubmitCard(
      handleSubmitPaymentMethod(
        stripe,
        elements,
        chosenBillingPlanType,
        numSeats,
        billingsStore.billingMethods?.data?.length > 0,
      ),
    );

    const breadcrumbNav = useMemo(() => {
      const nav = [
        {
          title: 'Choose a plan',
          href: '/workspace-settings/billing',
          id: '1',
          onClick: (event: SyntheticEvent<HTMLAnchorElement>) => {
            returnToPlanSelection(
              event,
              () => activateInactiveBillingTabs('plans'),
              () => updateShowNonActiveBilling(true),
              navigate,
            );
          },
        },
        {
          title: 'Checkout',
          href: '#',
          id: '2',
          isCurrent: true,
          onClick: () => {
            activateInactiveBillingTabs('billingCycle');
          },
        },
      ];
      return nav;
    }, [navigate, updateShowNonActiveBilling, activateInactiveBillingTabs]);

    const disableSavedButton = !isBillingAddressComplete(addressDetails, line1);

    const [numSeatsFormIsVisible] = useState<boolean>(false);

    const formatMoney = (money: number) =>
      Number.isFinite(money)
        ? money.toLocaleString(undefined, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })
        : '0.00';

    const disabledPaymentButton = !stripe;

    const billingPeriod = selectedBillingPlan === 'monthly' ? 'month' : 'year';
    const cardOptions = useMemo(
      () => getBillingCardOptions(theme.colors),
      [theme.colors],
    );

    return (
      <BillingCycleOuterWrapper onSubmit={handleSubmit}>
        <div className="billingBreadcrumb">
          <SecondaryBreadcrumb
            wrapperStyles={{marginBottom: 0}}
            navigation={breadcrumbNav}
          />
        </div>
        <BillingCycleInnerWrapper>
          <div className="checkoutColumn">
            <section className="checkoutCard">
              <Headline3 kind="textDark">Select billing cycle</Headline3>
              <VerticalSpacer size="8px" />
              <Body1 kind="textBody">
                Choose how you want to make your payment
              </Body1>
              <VerticalSpacer size="24px" />
              {isLifeTimePlan ? (
                <div className="flex flex-row justify-between items-center border border-borderDark rounded-[10px] p-4 mb-4">
                  <FlexRow>
                    <Body2 weight={'semibold'} kind={'textDark'}>
                      Lifetime{' '}
                    </Body2>{' '}
                    <CTA
                      style={{fontSize: 12}}
                      kind="purple300"
                      className="ml-1">
                      (save 75%)
                    </CTA>
                  </FlexRow>
                  <RadioField id="lifetime" checked />{' '}
                </div>
              ) : (
                <MultiRadioSelectDiv>
                  <div className="firstDiv">
                    <Body2
                      weight={
                        selectedBillingPlan === 'monthly'
                          ? 'semibold'
                          : 'regular'
                      }
                      kind={
                        selectedBillingPlan === 'monthly'
                          ? 'textDark'
                          : 'textBody'
                      }>
                      Pay monthly
                    </Body2>{' '}
                    <RadioField
                      id="monthly"
                      checked={selectedBillingPlan === 'monthly'}
                      onChange={() => setSelectedBillingPlan('monthly')}
                    />{' '}
                  </div>
                  <div className="secondDiv">
                    <div className="flex">
                      <Body2
                        weight={
                          selectedBillingPlan === 'yearly'
                            ? 'semibold'
                            : 'regular'
                        }
                        kind={
                          selectedBillingPlan === 'yearly'
                            ? 'textDark'
                            : 'textBody'
                        }>
                        Pay annually
                      </Body2>{' '}
                      <CTA
                        style={{fontSize: 12}}
                        kind="purple300"
                        className="ml-1">
                        (save 25%)
                      </CTA>
                    </div>
                    <RadioField
                      id="annually"
                      checked={selectedBillingPlan === 'yearly'}
                      onChange={() => setSelectedBillingPlan('yearly')}
                    />
                  </div>
                </MultiRadioSelectDiv>
              )}

              {!isFreeTierSubscription && !isLifeTimePlan && (
                <>
                  <VerticalSpacer size="24px" />
                  <Label>Discount code</Label>
                  <VerticalSpacer size="8px" />
                  <TextField
                    placeholder="Enter code here"
                    type="text"
                    name="discount_code"
                    actionStyle={{width: 60}}
                    value={coupon}
                    onChange={(e) => {
                      setCoupon(e.target.value);
                    }}
                    action={(ref) => (
                      <>
                        {coupon.length > 0 && couponDiscount > 0 && (
                          <span
                            ref={ref}
                            onClick={() => {
                              setCoupon('');
                              setCouponDiscount(0);
                              setValidCoupon('');
                            }}
                            style={{cursor: 'pointer'}}>
                            <CancelIcon />
                          </span>
                        )}
                        {coupon.length > 0 && couponDiscount === 0 && (
                          <Button
                            kind="primary"
                            width="xm"
                            onClick={() => validateCoupon(coupon)}
                            isLoading={
                              billingsStore.loader?.validateCoupon as boolean
                            }
                            type="button">
                            Apply
                          </Button>
                        )}
                      </>
                    )}
                  />
                </>
              )}
            </section>
            {billingsStore.billingMethods?.data?.length > 0 ? (
              <>
                <CardDetailsDiv>
                  <div className="title">
                    <Headline4 kind="textDark">Payment method</Headline4>
                  </div>
                  <div className="table-body">
                    <div className="header">
                      <Body2 kind="textBody">Card</Body2>
                      <Body2 kind="textBody">Expiry </Body2>
                      <Body2 kind="textBody">Actions </Body2>
                    </div>
                    {billingsStore.billingMethods?.data?.map(
                      (billingMethod: any) => (
                        <div className="content" key={billingMethod?.id}>
                          <Body2 kind="textBody">
                            {capitalize(billingMethod?.card?.brand)} ending in{' '}
                            {billingMethod?.card?.last4}
                          </Body2>
                          <Body2 kind="textBody">
                            {dayjs(
                              `2000-${billingMethod?.card?.exp_month}-01`,
                            ).format('MMMM')}
                            , {billingMethod?.card?.exp_year}
                          </Body2>
                          <Link
                            kind="blue"
                            style={{
                              color: '#585ADF',
                              textDecoration: 'none',
                              fontWeight: 500,
                            }}
                            onClick={() => setOpenDrawer(true)}
                            to="#">
                            Update Card
                          </Link>
                        </div>
                      ),
                    )}
                  </div>
                </CardDetailsDiv>
              </>
            ) : (
              <>
                <section className="checkoutCard">
                  <FlexRowSpaceBetween>
                    <Headline3 kind="textDark">
                      Enter billing information
                    </Headline3>
                    {addressSaved && (
                      <NoBorderButton
                        type="button"
                        onClick={() => setAddressSaved(false)}>
                        <Body2 weight="bold" kind="purple300">
                          Edit
                        </Body2>
                      </NoBorderButton>
                    )}
                  </FlexRowSpaceBetween>
                  {!addressSaved && (
                    <>
                      <VerticalSpacer size="8px" />
                      <Body1 kind="textBody">
                        Used to calculate tax and will appear on billing
                        documents.
                      </Body1>
                    </>
                  )}
                  <VerticalSpacer size="24px" />
                  <div className="address">
                    {!addressSaved ? (
                      <>
                        <Label>Address</Label>
                        <VerticalSpacer size="8px" />
                        <Controller
                          control={controlCard}
                          name="billing_details.address.line1"
                          defaultValue={line1}
                          render={() => <></>}
                        />
                        <TextField
                          placeholder="Enter address"
                          type="text"
                          value={line1}
                          inputStyle={{borderRadius: '10px'}}
                          onChange={(event) => {
                            setLine1(event.target.value);
                            handleFormValueChangeCard(
                              event.target.value,
                              'billing_details.address.line1',
                            );
                          }}
                          helper={
                            errorsCard.billing_details?.address?.line1
                              ?.message || ''
                          }
                          state={
                            errorsCard.billing_details?.address?.line1
                              ? 'error'
                              : 'default'
                          }
                        />
                        <Label>Country</Label>
                        <VerticalSpacer size="8px" />
                        <Controller
                          control={controlCard}
                          name="billing_details.address.country"
                          render={({onBlur}) => (
                            <SelectField
                              borderRadius="10px"
                              onBlur={onBlur}
                              name="billing_details.address.country"
                              defaultValue={addressDetails.country}
                              helper={
                                errorsCard.billing_details?.address?.country
                                  ?.message || ''
                              }
                              state={
                                errorsCard.billing_details?.address?.country
                                  ? 'error'
                                  : 'default'
                              }
                              onChange={(data: {
                                value: string;
                                isoCode: string;
                              }) => {
                                if (data?.value) {
                                  handleFormValueChangeCard(
                                    data.value,
                                    'billing_details.address.country',
                                  );
                                  setSelectedCountry(data.isoCode);
                                  updateAddressDetails('country', data.value);
                                } else {
                                  setSelectedCountry('');
                                  handleFormValueChangeCard(
                                    null,
                                    'billing_details.address.country',
                                  );
                                  updateAddressDetails('country', null);
                                }
                                handleFormValueChangeCard(
                                  null,
                                  'billing_details.address.city',
                                );
                                updateAddressDetails('city', null);
                              }}
                              placeholder="Select or search for a country"
                              options={countries?.map((country: ICountry) => ({
                                value: country.isoCode,
                                label: country.name,
                                isoCode: country.isoCode,
                              }))}
                            />
                          )}
                        />

                        <Label>City</Label>
                        <VerticalSpacer size="8px" />
                        <Controller
                          control={controlCard}
                          name="billing_details.address.city"
                          render={({onBlur, value}) => (
                            <SelectField
                              onBlur={onBlur}
                              borderRadius="10px"
                              name="billing_details.address.city"
                              value={value}
                              defaultValue={addressDetails.city}
                              helper={
                                errorsCard.billing_details?.address?.city
                                  ?.message || ''
                              }
                              state={
                                errorsCard.billing_details?.address?.city
                                  ? 'error'
                                  : 'default'
                              }
                              onChange={(data: {value: string}) => {
                                handleFormValueChangeCard(
                                  data?.value,
                                  'billing_details.address.city',
                                );
                                updateAddressDetails('city', data?.value);
                              }}
                              placeholder="Select or search for a city"
                              options={cities?.map((city: ICity) => ({
                                label: city.name,
                                value: city.name,
                              }))}
                            />
                          )}
                        />
                        <VerticalSpacer size="16px" />
                        <FlexRowEnd>
                          <BillingActionButton
                            type="button"
                            style={{padding: '8px 16px'}}
                            disabled={disableSavedButton}
                            onClick={handleAddressSave}>
                            Save address
                          </BillingActionButton>
                        </FlexRowEnd>
                      </>
                    ) : (
                      <Body2
                        weight="bold"
                        style={{textTransform: 'capitalize'}}>
                        {line1}, {addressDetails.city}, {addressDetails.country}
                      </Body2>
                    )}
                  </div>
                </section>

                <section className="checkoutCard">
                  <Headline3 kind="textDark">Enter card details</Headline3>
                  <VerticalSpacer size="16px" />
                  <CardInfoDiv>
                    <TextField
                      placeholder="Name on card"
                      type="text"
                      name="name"
                    />
                    <div className="stripeCard">
                      <CardElement
                        options={cardOptions}
                        onChange={(e) => {
                          setStripeError(e.error?.message);
                          setCardComplete(e.complete);
                        }}
                      />
                    </div>
                    <Helper
                      aria-label="helper"
                      state={stripeError ? 'error' : 'default'}
                      children={stripeError || ''}
                    />
                  </CardInfoDiv>
                </section>
              </>
            )}

            <BillingActionButton
              width="full"
              type={isSubmittingCard ? 'button' : 'submit'}
              onClick={isSubmittingCard ? () => false : undefined}
              disabled={
                billingsStore.billingMethods?.data?.length > 0
                  ? false
                  : disabledPaymentButton ||
                    !addressSaved ||
                    numSeatsFormIsVisible
              }
              isLoading={isSubmittingCard}
              style={{cursor: isSubmittingCard ? 'not-allowed' : undefined}}>
              {isFreeTierSubscription ? 'Start free trial' : 'Complete payment'}
            </BillingActionButton>
          </div>
          <div className="summaryColumn">
            <PriceBoxDiv>
              <div className="top">
                <Headline4 kind="textDark">Order summary</Headline4>
                <VerticalSpacer size="16px" />
                <FlexRowSpaceBetween>
                  <Body2>
                    Includes <span className="font-semibold">{numSeats}</span>{' '}
                    seat(s)
                  </Body2>

                  <Body2 weight="bold" className="mt-2">
                    {currencySymbol}
                    {formatMoney(subtotal + annualDiscountAmount)}
                  </Body2>
                </FlexRowSpaceBetween>
                <Body2 className="text-[10px]" kind="textBody">
                  {currencySymbol}
                  {formatMoney(
                    (subtotal + annualDiscountAmount) / (numSeats || 1),
                  )}{' '}
                  per seat
                </Body2>
              </div>
              <div className="bottom">
                {annualDiscountAmount > 0 && (
                  <div className="items">
                    <Body2 kind="textBody">
                      Annual saving ({annualDiscountPercent}%)
                    </Body2>
                    <CTA kind="textDark">
                      -{currencySymbol}
                      {formatMoney(annualDiscountAmount)}
                    </CTA>
                  </div>
                )}

                {couponDiscountAmount > 0 && (
                  <div className="items">
                    <Body2 kind="textBody">Promotion</Body2>
                    <CTA kind="textDark">
                      -{currencySymbol}
                      {formatMoney(couponDiscountAmount)}
                    </CTA>
                  </div>
                )}

                <div className="items">
                  <Body2 kind="textBody" weight="semibold">
                    Subtotal
                  </Body2>
                  <CTA kind="textDark">
                    {currencySymbol}
                    {formatMoney(subtotal - couponDiscountAmount)}
                  </CTA>
                </div>
                <div className="items">
                  <Body2 kind="textBody">Tax</Body2>
                  <CTA kind="textDark">Calculated at payment</CTA>
                </div>

                {/* <div className="hr" /> */}
                <div className="items border-t border-t-[#EDEDF2] pt-2">
                  <Body2 kind="textBody">Estimated due today</Body2>
                  <Headline2 kind="textDark">
                    {currencySymbol}{' '}
                    {isFreeTierSubscription ? 0 : formatMoney(dueToday)}
                  </Headline2>
                </div>
              </div>
            </PriceBoxDiv>
            <PriceHelperInfoDiv>
              {isFreeTierSubscription && (
                <>
                  <div className="info">
                    <span>
                      <ThickCheckIcon style={{stroke: '#585ADF'}} />
                    </span>
                    <HorizontalSpacer size="5px" />
                    <Body2 kind="textBody">
                      There is a{' '}
                      <span className="price">{currencySymbol}1 </span>
                      card validation fee, but rest assured, it will be
                      refunded.
                    </Body2>
                  </div>
                  <VerticalSpacer size="16px" />

                  <div className="info">
                    <span>
                      <ThickCheckIcon style={{stroke: '#585ADF'}} />
                    </span>
                    <HorizontalSpacer size="5px" />
                    <Body2 kind="textBody">
                      Starting {dayjs().add(14, 'days').format('MMMM DD')}, your
                      plan will begin and you will be charged{' '}
                      <span className="price">
                        {currencySymbol}
                        {formatMoney(dueToday)}
                      </span>{' '}
                      /{selectedBillingPlan} until when cancelled.
                    </Body2>
                  </div>
                  <VerticalSpacer size="16px" />

                  <div className="info">
                    <span>
                      <ThickCheckIcon style={{stroke: '#585ADF'}} />
                    </span>
                    <HorizontalSpacer size="5px" />
                    <Body2 kind="textBody">
                      You will be reminded 7 days before your trial ends.
                    </Body2>
                  </div>
                </>
              )}

              <UpdateCard
                open={openDrawer}
                onClose={() => setOpenDrawer(false)}
                stripe={stripe}
                elements={elements}
              />

              {!isFreeTierSubscription && (
                <div className="info">
                  <span>
                    <ThickCheckIcon style={{stroke: '#585ADF'}} />
                  </span>
                  <HorizontalSpacer size="5px" />
                  <Body2 kind="textBody">
                    Your card will be charged an estimated{' '}
                    <span className="price">
                      {currencySymbol}
                      {formatMoney(dueToday)}
                    </span>{' '}
                    now and each {billingPeriod} after.
                  </Body2>
                </div>
              )}

              <VerticalSpacer size="16px" />
              <div className="info">
                <span>
                  <ThickCheckIcon style={{stroke: '#585ADF'}} />
                </span>
                <HorizontalSpacer size="5px" />
                <Body2 kind="textBody">
                  New members use paid seats. If you exceed the seats included
                  in your subscription, Beam may apply a prorated charge for the
                  current billing period.
                </Body2>
              </div>
              <VerticalSpacer size="16px" />
              <div className="info">
                <span>
                  <ThickCheckIcon style={{stroke: '#585ADF'}} />
                </span>
                <HorizontalSpacer size="5px" />
                <Body2 kind="textBody">
                  Your account will be instantly updated. You can upgrade,
                  downgrade or cancel anytime.
                </Body2>
              </div>
            </PriceHelperInfoDiv>
          </div>
        </BillingCycleInnerWrapper>
      </BillingCycleOuterWrapper>
    );
  },
);

export const BillingCycle = observer(() => {
  return (
    <ElementsConsumer>
      {({stripe, elements}) => (
        <InjectedBillingCycle stripe={stripe} elements={elements} />
      )}
    </ElementsConsumer>
  );
});
